import { isBetaMode } from "@/lib/launch-mode";
import { useWaitlistCount, formatWaitlistCount } from "@/hooks/useWaitlistCount";

const testimonials = [
  {
    quote: "I went from Senior Engineer to Staff in 18 months. MomentumEQ kept me focused on the right skills—not random LinkedIn Learning courses. The 3E Framework forced me to balance learning, practice, and ownership. The AI skill gap analysis was eerily accurate.",
    name: "Alex Chen",
    role: "Staff Software Engineer",
    initials: "AC",
  },
  {
    quote: "The values discovery changed everything. I realized I was chasing 'Director' because I thought I should, not because it aligned with what I value (deep technical work + mentorship). I pivoted to Staff IC and I've never been happier or more effective.",
    name: "Sarah Martinez",
    role: "Product Manager → Senior IC",
    initials: "SM",
  },
  {
    quote: "I tried Notion templates, spreadsheets, even hired a coach. Nothing stuck until MomentumEQ. The habit tracking with streaks keeps me accountable daily, and the development plan gave me a clear path forward. 6 months in, I've shipped 3 major projects and got promoted to Engineering Manager.",
    name: "James Wong",
    role: "Engineering Manager",
    initials: "JW",
  },
];

const getStats = (betaMode: boolean, waitlistCount: string) => [
  { value: betaMode ? waitlistCount : "2,000+", label: betaMode ? "On Waitlist" : "Professionals" },
  { value: "5,000+", label: "Goals Set" },
  { value: "24", label: "Avg Streak" },
  { value: "92%", label: "Completion Rate" },
];

const TestimonialsSection = () => {
  const betaMode = isBetaMode();
  const { data: waitlistCount = 977 } = useWaitlistCount();
  const formattedCount = formatWaitlistCount(waitlistCount);
  const stats = getStats(betaMode, formattedCount);

  return (
    <section className="py-20 md:py-32 bg-[hsl(220,25%,6%)] relative">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[150px]" />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[400px] h-[400px] bg-accent/5 rounded-full blur-[120px]" />
      </div>

      <div className="container relative z-10">
        {/* Section Heading */}
        <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-16">
          {betaMode ? (
            <>Join <span className="gradient-text">{formattedCount} Professionals</span> on the Waitlist</>
          ) : (
            <>Join <span className="gradient-text">2,000+ Professionals</span> Building Momentum</>
          )}
        </h2>

        {/* Testimonial Cards */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="relative bg-card/50 rounded-xl p-6 md:p-8 flex flex-col"
            >
              {/* Gradient left border */}
              <div className="absolute left-0 top-4 bottom-4 w-1 rounded-full bg-gradient-to-b from-primary to-accent" />

              {/* Avatar */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                  <span className="text-lg font-semibold text-muted-foreground">
                    {testimonial.initials}
                  </span>
                </div>
              </div>

              {/* Quote */}
              <blockquote className="text-foreground/90 text-base md:text-lg leading-relaxed flex-1 mb-6">
                "{testimonial.quote}"
              </blockquote>

              {/* Attribution */}
              <div className="text-center">
                <p className="font-semibold text-foreground">— {testimonial.name}</p>
                <p className="text-sm text-muted-foreground">{testimonial.role}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-4xl mx-auto">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <p className="text-3xl md:text-4xl font-display font-bold gradient-text">
                {stat.value}
              </p>
              <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
